/*eslint max-lines-per-function: ["error", 176]*/

import type { TFunction } from 'i18next';

import { GridRecord } from '../../components/Grid/GridDataTypes';
import {
  CreateUpdateEmailsWithFVSDto,
  CreateUpdateEmailWithMultipleFVDto,
  CreateUpdateInvoiceEstimateItemDto,
  CreateUpdateInvoiceEstimateItemDtoFromJSON,
  CreateUpdateInvoiceEstimateItemsDto,
  CreateUpdateSalesInvoiceDto,
  CreateUpdateSalesInvoiceDtoFromJSON,
  CreateUpdateSalesInvoicePositionDto,
  CreateUpdateSalesInvoicePositionDtoFromJSON,
  CreateUpdateSalesInvoicePositionsDto,
  CreateUpdateSummarySalesInvoiceDto,
  DefaultApi,
  GetDictionaryDto,
  GetEstimateItemDto,
  GetInvoiceEstimateItemDto,
  GetLastEditorDto,
  GetNumberingDto,
  GetProjectDto,
  GetSalesInvoiceDto,
  GetSalesInvoiceDtoTypeEnum,
  GetSalesInvoiceForItem,
  GetSalesInvoicePositionDto,
  GetSalesInvoiceWithAmountDto,
} from '../autogenerated/pokApiClient';
import { salesInvoiceForGrid } from '../../../pok/components/SalesInvoices/SalesInvoiceSearch';
import {
  InvoiceEstimateItemSchema,
  SalesInvoicePositionSchema,
  SalesInvoiceSchema,
  SalesInvoiceSummarySchema,
} from '../validation/schemas';
import { validateAgainst } from '../validation/validateAgainst';
import mathUtils from '../../../utils/mathUtils';
import {
  calculateEstimateItemCommission,
  calculateEstimateItemSalesNet,
} from '../../../pok/components/SalesInvoices/calculateSalesInvoiceAmountsUtils';
import {
  getMainCurrencySymbol,
  isMainCurrencyPLN,
  getMainCurrency,
} from '../../../utils/currencyFormatUtils';
import { TKeys } from '../../../translations';
import {
  CurrencyIsoTypeEnum,
  SalesInvoiceStatusEnum,
  InvoiceTypeEnum,
  PaymentFormEnum,
  VatEnum,
  KSFStatusEnum,
} from '../validation/enums';
import { sendFVSByEmailSearchGrid } from '../../../pok/components/SendFVSByEmailSearch/SendFVSByEmailSearch';
import { alreadySentEmailsSearchGrid } from '../../../pok/components/SendFVSByEmailSearch/AlreadySentEmailsSearch';

export interface SalesInvoicesContextInterface {
  getProjectsIds: (id: string) => Promise<string[]>;
  getNumberToCorrect: (id: string) => Promise<string>;
  getWithSalesInvoicePositions: (
    id: string,
    withJoin?: boolean,
  ) => Promise<GetSalesInvoiceDto>;
  getTeamIdsAndCompanyId: (
    id: string,
  ) => Promise<{ id: string; companyId: string; teamIds?: string[] }>;
  getAllByPortion: (
    t: TFunction<'fvsLng', 'fvsLng'>,
    tk: TKeys<'fvsLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    status?: SalesInvoiceStatusEnum,
    statusKsef?: KSFStatusEnum,
  ) => Promise<GridRecord[]>;
  getAllCount: (
    filterText?: string,
    filterColumns?: string,
    status?: SalesInvoiceStatusEnum,
    statusKsef?: KSFStatusEnum,
  ) => Promise<number>;
  getAllByPortionByNegativePositionsDiff: (
    t: TFunction<'fvsLng', 'fvsLng'>,
    tk: TKeys<'fvsLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
  ) => Promise<GridRecord[]>;
  getAllForCompanyYearByPortion: (
    t: TFunction<'fvsLng', 'fvsLng'>,
    tk: TKeys<'fvsLng'>,
    companyId: string,
    status: SalesInvoiceStatusEnum[],
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    year?: number,
  ) => Promise<GridRecord[]>;
  getAllCountByNegativePositionsDiff: (filterText?: string) => Promise<number>;
  getAllForCompanyYearCount: (
    companyId: string,
    status: SalesInvoiceStatusEnum[],
    filterText?: string,
    year?: number,
  ) => Promise<number>;
  getAllByPackageXML: (
    t: TFunction<'fvsLng', 'fvsLng'>,
    tk: TKeys<'fvsLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    packageXMLId?: string,
  ) => Promise<GridRecord[]>;
  getCountByPackageXML: (
    filterText: string,
    packageXMLId?: string,
  ) => Promise<number>;
  getAllReadyToSentWithoutCorrectByPortion: (
    t: TFunction<'fvsLng', 'fvsLng'>,
    tk: TKeys<'fvsLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    type?: InvoiceTypeEnum,
  ) => Promise<GridRecord[]>;
  getAllReadyToSentWithoutCorrectCount: (
    filterText?: string,
    filterColumns?: string,
    type?: InvoiceTypeEnum,
  ) => Promise<number>;
  getAllSentToSymphonyByPortion: (
    t: TFunction<'fvsLng', 'fvsLng'>,
    tk: TKeys<'fvsLng'>,
    sentToSymphonyHandel: boolean,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllSentToSymphonyCount: (
    sentToSymphonyHandel: boolean,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  update: (id: string, dto: CreateUpdateSalesInvoiceDto) => Promise<void>;
  updateSummary: (
    id: string,
    dto: CreateUpdateSummarySalesInvoiceDto,
  ) => Promise<GetSalesInvoiceDto>;
  create: (dto: CreateUpdateSalesInvoiceDto) => Promise<GetSalesInvoiceDto>;
  createCorrection: (parentId: string) => Promise<GetSalesInvoiceDto>;
  deactivate: (id: string) => Promise<void>;
  createEstimateItemPositions: (
    dto: CreateUpdateInvoiceEstimateItemsDto,
  ) => Promise<GetInvoiceEstimateItemDto[]>;
  createPositions: (
    dto: CreateUpdateSalesInvoicePositionsDto,
  ) => Promise<GetSalesInvoicePositionDto[]>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  getByEstimateItem: (
    estimateItemId: string,
  ) => Promise<GetSalesInvoiceForItem[]>;
  getByProject: (projectId: string) => Promise<GetSalesInvoiceWithAmountDto[]>;
  findAvailableInvoiceNumbers: (
    companyId: string,
    isCorrection: boolean,
    type: InvoiceTypeEnum,
    currency: CurrencyIsoTypeEnum,
    invoiceDate: Date,
  ) => Promise<GetNumberingDto[]>;

  getWaitingToSendByEmailByPortion: (
    t: TFunction<'fvsLng', 'fvsLng'>,
    tk: TKeys<'fvsLng'>,
    refresh: boolean,
    setRefresh: (refresh: boolean) => void,
    withEmailAddress: boolean,
    withOneEmailAddress: boolean,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getWaitingToSendByEmailCount: (
    withEmailAddress: boolean,
    withOneEmailAddress: boolean,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;

  getWithSentEmailByPortion: (
    refreshData: () => void,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getWithSentEmailsCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;

  sendFVSByEmails: (body: CreateUpdateEmailsWithFVSDto) => Promise<void>;
  sendMultipleFVSInOneEmail: (
    body: CreateUpdateEmailWithMultipleFVDto,
  ) => Promise<void>;

  updateEmailText: (
    id: string,
    emailText?: string | null,
    emailSubject?: string | null,
  ) => Promise<GetSalesInvoiceDto>;
  updateEmailAddress: (
    salesInvoiceId: string,
    emailAddressId: string,
  ) => Promise<GetSalesInvoiceDto>;
}

// eslint-disable-next-line max-lines-per-function
export const SalesInvoiceContext = (
  api: DefaultApi,
): SalesInvoicesContextInterface => {
  return {
    getProjectsIds: (id: string) =>
      api.salesInvoiceControllerGetProjectsIds(id),
    getNumberToCorrect: (id: string) =>
      api.salesInvoiceControllerGetNumberToCorrect(id),
    getWithSalesInvoicePositions: (id: string, withJoin?: boolean) =>
      api.salesInvoiceControllerGetWithSalesInvoicePositions(id, withJoin),
    getTeamIdsAndCompanyId: (id: string) =>
      api.salesInvoiceControllerGetTeamIdsAndCompanyId(id),
    getAllByPortion: async (
      t: TFunction<'fvsLng', 'fvsLng'>,
      tk: TKeys<'fvsLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      status?: SalesInvoiceStatusEnum,
      statusKsef?: KSFStatusEnum,
    ) => {
      const data = await api.salesInvoiceControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
        status?.toString(),
        statusKsef?.toString(),
      );

      return data.map(o => salesInvoiceForGrid(o, t, tk));
    },
    getAllCount: (
      filterText?: string,
      filterColumns?: string,
      status?: SalesInvoiceStatusEnum,
      statusKsef?: KSFStatusEnum,
    ) => {
      return api.salesInvoiceControllerGetAllCount(
        filterText || '',
        filterColumns,
        status?.toString(),
        statusKsef?.toString(),
      );
    },
    getAllByPortionByNegativePositionsDiff: async (
      t: TFunction<'fvsLng', 'fvsLng'>,
      tk: TKeys<'fvsLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) => {
      const data =
        await api.salesInvoiceControllerGetAllByPortionByNegativePositionsDiff(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
        );

      return data.map(o => salesInvoiceForGrid(o, t, tk));
    },
    getAllForCompanyYearByPortion: async (
      t: TFunction<'fvsLng', 'fvsLng'>,
      tk: TKeys<'fvsLng'>,
      companyId: string,
      status: SalesInvoiceStatusEnum[],
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      year?: number,
    ) => {
      const data =
        await api.salesInvoiceControllerGetAllForCompanyYearByPortion(
          companyId,
          status?.map(v => v.toString()) || [''],
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          year,
        );

      return data.map(o => salesInvoiceForGrid(o, t, tk));
    },
    getAllCountByNegativePositionsDiff: (filterText?: string) => {
      return api.salesInvoiceControllerGetAllCountByNegativePositionsDiff(
        filterText || '',
      );
    },
    getAllForCompanyYearCount: (
      companyId: string,
      status: SalesInvoiceStatusEnum[],
      filterText?: string,
      year?: number,
    ) => {
      return api.salesInvoiceControllerGetAllForCompanyYearCount(
        companyId,
        status?.map(v => v.toString()) || [''],
        filterText || '',
        year,
      );
    },
    getAllByPackageXML: async (
      t: TFunction<'fvsLng', 'fvsLng'>,
      tk: TKeys<'fvsLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      packageXMLId?: string,
    ) => {
      const data = await api.salesInvoiceControllerGetAllByPackageXMLByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        packageXMLId || '',
      );

      return data.map(o => salesInvoiceForGrid(o, t, tk));
    },
    getCountByPackageXML: (filterText?: string, packageXMLId?: string) => {
      return api.salesInvoiceControllerGetCountByPackageXML(
        filterText || '',
        packageXMLId || '',
      );
    },
    getAllReadyToSentWithoutCorrectByPortion: async (
      t: TFunction<'fvsLng', 'fvsLng'>,
      tk: TKeys<'fvsLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      type?: InvoiceTypeEnum,
    ) => {
      const data =
        await api.salesInvoiceControllerGetAllReadyToSentWithoutCorrectByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
          type,
        );

      return data.map(o => salesInvoiceForGrid(o, t, tk));
    },
    getAllReadyToSentWithoutCorrectCount: (
      filterText?: string,
      filterColumns?: string,
      type?: InvoiceTypeEnum,
    ) => {
      return api.salesInvoiceControllerGetAllReadyToSentWithoutCorrectCount(
        filterText || '',
        filterColumns,
        type,
      );
    },
    getAllSentToSymphonyByPortion: async (
      t: TFunction<'fvsLng', 'fvsLng'>,
      tk: TKeys<'fvsLng'>,
      sentToSymphonyHandel: boolean,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.salesInvoiceControllerGetAllSentToSymphonyByPortion(
          sentToSymphonyHandel,
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
        );

      return data.map(o => salesInvoiceForGrid(o, t, tk));
    },
    getAllSentToSymphonyCount: (
      sentToSymphonyHandel: boolean,
      filterText?: string,
      filterColumns?: string,
    ) => {
      return api.salesInvoiceControllerGetAllSentToSymphonyCount(
        sentToSymphonyHandel,
        filterText || '',
        filterColumns,
      );
    },
    update: (id: string, dto: CreateUpdateSalesInvoiceDto) =>
      api.salesInvoiceControllerUpdate(id, dto),
    updateSummary: (id: string, dto: CreateUpdateSummarySalesInvoiceDto) =>
      api.salesInvoiceControllerUpdateSummary(id, dto),
    create: (dto: CreateUpdateSalesInvoiceDto) =>
      api.salesInvoiceControllerCreate(dto),
    createCorrection: (parentId: string) =>
      api.salesInvoiceControllerCreateCorrection({ parentId }),
    deactivate: (id: string) => api.salesInvoiceControllerDeactivate(id),
    createEstimateItemPositions: (dto: CreateUpdateInvoiceEstimateItemsDto) =>
      api.invoiceEstimateItemControllerCreateMany(dto),
    createPositions: (dto: CreateUpdateSalesInvoicePositionsDto) =>
      api.salesInvoicePositionControllerCreateMany(dto),
    getLastEditor: (id: string) => api.salesInvoiceControllerGetLastEditor(id),
    getByEstimateItem: (estimateItemId: string) =>
      api.salesInvoiceControllerFindByEstimateItem(estimateItemId),
    getByProject: (projectId: string) =>
      api.salesInvoiceControllerFindByProject(projectId),
    findAvailableInvoiceNumbers: (
      companyId: string,
      isCorrection: boolean,
      type: InvoiceTypeEnum,
      currency: CurrencyIsoTypeEnum,
      invoiceDate: Date,
    ) =>
      api.salesInvoiceControllerFindAvailableInvoiceNumbers(
        companyId,
        isCorrection,
        type,
        currency,
        invoiceDate,
      ),
    getWaitingToSendByEmailByPortion: async (
      t: TFunction<'fvsLng', 'fvsLng'>,
      tk: TKeys<'fvsLng'>,
      refresh: boolean,
      setRefresh: (refresh: boolean) => void,
      withEmailAddress: boolean,
      withOneEmailAddress: boolean,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.salesInvoiceControllerGetWaitingToSendByEmailByPortion(
          withEmailAddress,
          withOneEmailAddress,
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
        );

      return data.map(o =>
        sendFVSByEmailSearchGrid(o, t, tk, refresh, setRefresh),
      );
    },
    getWaitingToSendByEmailCount: (
      withEmailAddress: boolean,
      withOneEmailAddress: boolean,
      filterText?: string,
      filterColumns?: string,
    ) => {
      return api.salesInvoiceControllerGetWaitingToSendByEmailCount(
        withEmailAddress,
        withOneEmailAddress,
        filterText || '',
        filterColumns,
      );
    },

    getWithSentEmailByPortion: async (
      refreshData: () => void,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.salesInvoiceControllerGetWithSentEmailsByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );

      return data.map(o => alreadySentEmailsSearchGrid(o, refreshData));
    },
    getWithSentEmailsCount: (filterText?: string, filterColumns?: string) => {
      return api.salesInvoiceControllerGetWithSentEmailsCount(
        filterText || '',
        filterColumns,
      );
    },

    sendFVSByEmails: (body: CreateUpdateEmailsWithFVSDto) =>
      api.salesInvoiceControllerSendFVSByEmails(body),

    sendMultipleFVSInOneEmail: (body: CreateUpdateEmailWithMultipleFVDto) =>
      api.salesInvoiceControllerSendMultipleFVSInOneEmail(body),

    updateEmailText: (
      id: string,
      emailText?: string | null,
      emailSubject?: string | null,
    ) =>
      api.salesInvoiceControllerUpdateEmailText(id, {
        emailText,
        emailSubject,
      }),

    updateEmailAddress: (salesInvoiceId: string, emailAddressId: string) =>
      api.salesInvoiceControllerUpdateEmailAddress(
        salesInvoiceId,
        emailAddressId,
      ),
  };
};

const isDomestic = (project: GetProjectDto) => {
  if (isMainCurrencyPLN()) {
    return project?.purchaser?.client.country.isMain;
  }

  //TODO: dorobić dla spółek zagranicznych
  return true;
};

export const newSalesInvoice = (
  project: GetProjectDto,
  isInternalClient?: boolean,
  gtuDefaultCodeId?: string,
  documentDefaultIds?: string[],
  payableDaysList?: GetDictionaryDto[],
) => {
  const isClientRelated = project?.purchaser.client.isRelated;
  const domestic = isDomestic(project);
  const payableDays = payableDaysList?.find(({ value }) =>
    isInternalClient ? value === '60' : value === '14',
  );

  return CreateUpdateSalesInvoiceDtoFromJSON({
    active: true,
    projectIds: [project.id],
    type: domestic ? InvoiceTypeEnum.Domestic : InvoiceTypeEnum.Foreign,
    currency: getMainCurrencySymbol(),
    status: SalesInvoiceStatusEnum.Draft,
    paymentForm: PaymentFormEnum.BankTransfer,
    invoiceDate: new Date(),
    saleDate: new Date(),
    payableDaysId: payableDays?.id,
    printProjectNumber: true,
    isPaymentFromReceiptDate: false,
    gtuCodeId: gtuDefaultCodeId,
    documentCodeIds: isClientRelated ? documentDefaultIds : [],
    purchaserId: project?.purchaser?.id,
  });
};

export const newInvoiceEstimateItem = (
  json: CreateUpdateInvoiceEstimateItemDto,
) => CreateUpdateInvoiceEstimateItemDtoFromJSON(json);

export const newInvoicePosition = (json: CreateUpdateSalesInvoicePositionDto) =>
  CreateUpdateSalesInvoicePositionDtoFromJSON(json);

export const parseEstimateItemPositionToInvoicePosition = (
  json: GetInvoiceEstimateItemDto,
  order: number,
  type: GetSalesInvoiceDtoTypeEnum,
  projectId?: string,
  year?: number,
  internal?: boolean,
): CreateUpdateSalesInvoicePositionDto[] => {
  const salesFinancialAccount =
    json.estimateItem.profileCategory?.profileCategoriesYears?.filter(
      o =>
        o.year === year &&
        (o.internal === internal ||
          o.internal === null ||
          o.internal === undefined) &&
        o.active === true,
    )[0].salesFinancialAccount || '';
  const commissionFinancialAccount =
    json.estimateItem.profileCategory?.profileCategoriesYears?.filter(
      o =>
        o.year === year &&
        (o.internal === internal ||
          o.internal === null ||
          o.internal === undefined) &&
        o.active === true,
    )[0].commissionFinancialAccount || '';

  const vat = getVat(type);
  if (salesFinancialAccount !== commissionFinancialAccount) {
    const result = [];
    const isAmount = mathUtils.Big(json.amount).toNumber() > 0;
    const isCommissionAmount =
      mathUtils.Big(json.commissionAmount).toNumber() > 0;

    if (isAmount) {
      result.push(
        newInvoicePosition({
          order,
          name: json.estimateItem.position.name,
          amount: json.amount,
          vat: vat,
          salesInvoiceId: json.salesInvoiceId,
          financialAccount: json.estimateItem.profileCategory?.name,
          profileCategoryId: json.estimateItem.profileCategory.id,
          projectId: projectId || '',
        }),
      );
    }

    if (isCommissionAmount) {
      result.push(
        newInvoicePosition({
          order: isAmount ? order + 1 : order,
          name: `${json.estimateItem.position.name} (Prowizja)`,
          amount: json.commissionAmount,
          vat: vat,
          salesInvoiceId: json.salesInvoiceId,
          financialAccount: `${json.estimateItem.profileCategory?.name} (pr.)`,
          profileCategoryId: json.estimateItem.profileCategory.id,
          projectId: projectId || '',
        }),
      );
    }

    return result;
  }

  return [
    newInvoicePosition({
      order,
      name: json.estimateItem.position.name,
      amount: mathUtils.add(json.amount, json.commissionAmount).toString(),
      vat: vat,
      salesInvoiceId: json.salesInvoiceId,
      financialAccount: json.estimateItem.profileCategory?.name,
      profileCategoryId: json.estimateItem.profileCategory.id,
      projectId: projectId || '',
    }),
  ];
};

export const validateInvoiceEstimateItem = (
  invoiceEstimateItem: CreateUpdateInvoiceEstimateItemDto,
) => validateAgainst(InvoiceEstimateItemSchema, invoiceEstimateItem);

export const validateInvoiceEstimateItems = async (
  t: TFunction<'fvsLng', 'fvsLng'>,
  tk: TKeys<'fvsLng'>,
  salesInvoiceEstimateItems: CreateUpdateInvoiceEstimateItemDto[],
  availableEstimateItems?: GetEstimateItemDto[],
  salesInvoice?: GetSalesInvoiceDto,
): Promise<{ isValid: boolean; errors: string[] }> => {
  const isCorrection = !!salesInvoice?.parent;
  const errors: string[] = [];

  const validationResults = await Promise.all(
    salesInvoiceEstimateItems.map(async invoiceEstimateItem => ({
      ...(await validateInvoiceEstimateItem(invoiceEstimateItem)),
      item: invoiceEstimateItem,
    })),
  );
  const isMoreThanZeroEstimateItems = salesInvoiceEstimateItems.length > 0;
  const isAtLeastOneValueInPositionsFilled =
    isCorrection ||
    salesInvoiceEstimateItems.some(
      item =>
        mathUtils.Big(item.amount).gt(0) ||
        mathUtils.Big(item.commissionAmount).gt(0),
    );

  if (!isMoreThanZeroEstimateItems) {
    errors.push(t(tk.finance.noPositionsToInvoice));
  }

  if (!isAtLeastOneValueInPositionsFilled) {
    errors.push(t(tk.finance.atLeastOneValueInPositionsFilled));
  }

  const isValid =
    validationResults.every(result => result.valid) &&
    isMoreThanZeroEstimateItems &&
    isAtLeastOneValueInPositionsFilled;

  validationResults.forEach(result => {
    if (!result.valid) {
      const item = availableEstimateItems?.find(
        estimateItem => estimateItem.id === result.item.estimateItemId,
      );
      errors.push(
        ...result.errors.map(
          error =>
            `${t(tk.finance.position)} '${item?.position?.name}': ${error}`,
        ),
      );
    }
  });

  return {
    isValid,
    errors,
  };
};

export const formatOriginalSalesInvoiceEstimateItem = (
  estimateItem: GetEstimateItemDto,
  salesInvoice?: GetSalesInvoiceDto,
) => {
  const amount =
    salesInvoice?.invoiceEstimateItems?.find(
      invoiceEstimateItem =>
        invoiceEstimateItem.estimateItem.id === estimateItem.id,
    )?.amount || '0';

  const commissionAmount =
    salesInvoice?.invoiceEstimateItems?.find(
      invoiceEstimateItem =>
        invoiceEstimateItem.estimateItem.id === estimateItem.id,
    )?.commissionAmount || '0';

  return newInvoiceEstimateItem({
    estimateItemId: estimateItem.id,
    amount,
    commissionAmount,
    salesInvoiceId: salesInvoice?.id || '',
  });
};

export const formatSalesInvoiceEstimateItem = (
  estimateItem: GetEstimateItemDto,
  salesInvoice?: GetSalesInvoiceDto,
  originalEstimateItems?: CreateUpdateInvoiceEstimateItemDto[],
) => {
  const shouldFillDefaultValues = !salesInvoice?.invoiceEstimateItems?.length;

  const amount = shouldFillDefaultValues
    ? calculateEstimateItemSalesNet(
        estimateItem,
        originalEstimateItems,
      ).toString()
    : salesInvoice?.invoiceEstimateItems?.find(
        invoiceEstimateItem =>
          invoiceEstimateItem.estimateItem.id === estimateItem.id,
      )?.amount;

  const commissionAmount = shouldFillDefaultValues
    ? calculateEstimateItemCommission(
        estimateItem,
        originalEstimateItems,
      ).toString()
    : salesInvoice?.invoiceEstimateItems?.find(
        invoiceEstimateItem =>
          invoiceEstimateItem.estimateItem.id === estimateItem.id,
      )?.commissionAmount;

  return newInvoiceEstimateItem({
    estimateItemId: estimateItem.id,
    amount: amount || '0',
    commissionAmount: commissionAmount || '0',
    salesInvoiceId: salesInvoice?.id || '',
  });
};

export const validateInvoicePosition = (
  position: CreateUpdateSalesInvoicePositionDto,
) => validateAgainst(SalesInvoicePositionSchema, position);

export const validate = (salesInvoice: CreateUpdateSalesInvoiceDto) =>
  validateAgainst(SalesInvoiceSchema, salesInvoice);

export const validateSummary = (
  salesInvoice: CreateUpdateSummarySalesInvoiceDto,
) => validateAgainst(SalesInvoiceSummarySchema, salesInvoice);

export const convert = (
  salesInvoice?: GetSalesInvoiceDto,
  project?: GetProjectDto,
  isInternalClient?: boolean,
  gtuDefaultCodeId?: string,
  documentDefaultIds?: string[],
  payableDaysList?: GetDictionaryDto[],
) => {
  if (!salesInvoice && project) {
    return newSalesInvoice(
      project,
      isInternalClient,
      gtuDefaultCodeId,
      documentDefaultIds,
      payableDaysList,
    );
  }
  const converted = CreateUpdateSalesInvoiceDtoFromJSON(salesInvoice);
  if (salesInvoice) {
    converted.projectIds =
      salesInvoice?.salesInvoiceProjects?.map(({ project }) => project.id) ||
      [];
    converted.purchaserId = salesInvoice.purchaser.id;
    converted.companyId = salesInvoice.company.id;
    converted.payableDaysId = salesInvoice.payableDays?.id;
    converted.correctionReasonDictId = salesInvoice.correctionReasonDict?.id;
    converted.recipientClientId = salesInvoice.recipientClient?.id;
  }

  if (converted) {
    converted.parentId = salesInvoice?.parent?.id;
  }
  return converted;
};
export const convertInvoicePosition = (
  position: GetSalesInvoicePositionDto,
) => {
  const converted = CreateUpdateSalesInvoicePositionDtoFromJSON(position);
  converted.profileCategoryId = position.profileCategory?.id;
  converted.salesInvoiceId = position.salesInvoice?.id;

  return converted;
};

const checkIsZeroInvoice = (salesInvoice: GetSalesInvoiceDto) => {
  return (
    !!salesInvoice?.parent &&
    salesInvoice?.invoiceEstimateItems?.every(
      ({ amount, commissionAmount }) =>
        (amount === '0' && commissionAmount === '0') ||
        (!amount && !commissionAmount),
    )
  );
};

export const buildPositionsFromInvoiceEstimateItems = (
  salesInvoice?: GetSalesInvoiceDto,
  parentPositions?: GetSalesInvoicePositionDto[],
  estimateItems?: GetEstimateItemDto[],
) => {
  if (!salesInvoice?.invoiceEstimateItems) {
    return [];
  }

  const isZeroInvoice = checkIsZeroInvoice(salesInvoice);
  if (isZeroInvoice) {
    return (
      parentPositions?.map(position =>
        convertInvoicePosition({
          ...position,
          amount: '0',
        }),
      ) || []
    );
  }

  return salesInvoice.invoiceEstimateItems.reduce(
    (acc: CreateUpdateSalesInvoicePositionDto[], item) => {
      const startOrder = acc.length > 0 ? acc[acc.length - 1].order + 1 : 1;
      const foundProject = estimateItems?.find(
        estimateItem => estimateItem.id === item.estimateItem.id,
      )?.estimateByMonth.project;

      const newPositions = parseEstimateItemPositionToInvoicePosition(
        item,
        startOrder,
        salesInvoice.type,
        foundProject?.id,
        salesInvoice.saleDate.getFullYear(),
        salesInvoice.purchaser.client.internal,
      );

      if (newPositions.length > 1) {
        newPositions.forEach((np, npIndex) => {
          np.order = startOrder + npIndex;
        });
      }

      return acc.concat(newPositions);
    },
    [],
  );
};

const getVat = (type: string) => {
  const curr = getMainCurrency();
  if (curr.countryCode === 'pl-PL') {
    return type === GetSalesInvoiceDtoTypeEnum.Foreign
      ? VatEnum['np.']
      : VatEnum['23%'];
  }
  if (curr.countryCode === 'de-DE') {
    return VatEnum['19%'];
  }
  if (curr.countryCode === 'en-US') {
    return VatEnum['np.'];
  }
  return VatEnum['23%'];
};
